'use strict';

(function (angular) {
    dataService.$inject = ['$window', '$q', '$log', '$sce', '$timeout', 'basketService', 'productService', 'promotionService', 'BASKET_ID'];
    angular
        .module('jwsdw.basket')
        .factory('dataService', dataService);

    /**
     * @class jwsdwBasket.dataService
     * @description Service that provides access to the data layer of the basket
     * @param {Object} $window window service that provides access to the window object
     * @param {Object} $q promise service that provides promise functionality
     * @param {Object} $log log service that provides logging in angluar
     * @param {Object} $sce angular santize service
     * @param {Object} $timeout angular timeout service
     * @param {Object} basketService service to provide basket information
     * @param {Object} productService service to provide product information (used for creation of variants item)
     * @param {Object} promotionService service to provide promotion information
     * @param {String} BASKET_ID basket id of the current basket provided through demandware backend (window.jwsdwSettings) and loaded into the module as constant
     * @returns {Object} service object that returns public methods
     */
    function dataService($window, $q, $log, $sce, $timeout, basketService, productService, promotionService, BASKET_ID) { // eslint-disable-line max-statements
        var service,
            _ready = false,
            _voucherDataReady = false,
            _voucherSectionTransition = false,
            _voucherRetrievalFailed = false,
            _callback,
            _basketData,
            _voucherData,
            _totalOrderPriceAdjustment,
            _quantityErrors = {};

        //public methods
        service = {
            'quantityError': false,
            'init': init,
            'ready': ready,
            'voucherDataReady': voucherDataReady,
            'voucherSectionTransition': voucherSectionTransition,
            'voucherRetrievalFailed': voucherRetrievalFailed,
            'empty': empty,
            'update': update,
            'basketSuccess': basketSuccess,
            'voucherDataSuccess': voucherDataSuccess,
            'voucherDataError': voucherDataError,
            'orderTotal': orderTotal,
            'merchandizeTotalGrossPrice': merchandizeTotalGrossPrice,
            'adjustedMerchandizeTotalPrice': adjustedMerchandizeTotalPrice,
            'shippingTotal': shippingTotal,
            'totalOrderPriceAdjustment': totalOrderPriceAdjustment,
            'productLevelPriceAdjustments': productLevelPriceAdjustments,
            'orderCouponPriceAdjustments': orderCouponPriceAdjustments,
            'orderPriceAdjustments': orderPriceAdjustments,
            'productItems': productItems,
            'couponItems': couponItems,
            'couponGiftCertificateItems': couponGiftCertificateItems,
            'loyaltyCouponItems': loyaltyCouponItems,
            'giftCardItems': giftCardItems,
            'isReducedProduct': isReducedProduct,
            'isTaxIncluded': isTaxIncluded,
            'getTaxRate': getTaxRate,
            'isMixedTaxRate': isMixedTaxRate,
            'hasItemDiscount': hasItemDiscount,
            'productQuantity': productQuantity,
            'checkValidQuantity': checkValidQuantity,
            'freeShippingAmount': freeShippingAmount,
            'freeShippingPercentage': freeShippingPercentage,
            'isChoiceOfBonusProductLineItem': isChoiceOfBonusProductLineItem,
            'applicablePaymentMethods': applicablePaymentMethods,
            'isLoyaltyCustomer': isLoyaltyCustomer,
            'isLoyaltyCustomerForCurrentCountry': isLoyaltyCustomerForCurrentCountry,
            'isEmployeeAccount': isEmployeeAccount,
            'isCallawayAccount': isCallawayAccount,
            'isFranchiseStore': isFranchiseStore,
            'sessionCurrency': sessionCurrency,
            'agent': agent,
            'isCustomerLoggedIn': isCustomerLoggedIn,
            'getOriginalOrderTotal': getOriginalOrderTotal,
            'errorMessages': errorMessages,
            'recommendedProductItem': recommendedProductItem,
            'promotionTotal': promotionTotal,
            'orderLimitReached': orderLimitReached,
            'refusalReason': refusalReason,
            'orderLimitMsg': orderLimitMsg,
            'showShippingNotification': showShippingNotification,
            'selectableBonusProductsList': selectableBonusProducts,
            'hasBonusDiscountLineItem': hasBonusDiscountLineItem,
            'hideBonusProductImage': hideBonusProductImage,
            'maxBonusProducts': maxBonusProducts,
            'isRemovedBonusProduct': isRemovedBonusProduct,
            'defaultSelectableBonusProduct': defaultSelectableBonusProduct,
            'defaultBonusProducts': defaultBonusProducts,
            'selectedBonusProductLineItems': selectedBonusProductLineItems,
            'outOfStockBonus': outOfStockBonus,
            'hasPriceAdjustments': hasPriceAdjustments,
            'approachingDiscounts': approachingDiscounts,
            'hasVouchersAvailable': hasVouchersAvailable,
            'hasVouchersActivated': hasVouchersActivated,
            'activatedVouchers': activatedVouchers,
            'eligibleVouchers': eligibleVouchers,
            'basketVouchers': basketVouchers,
            'openSelectVoucherPicker': openSelectVoucherPicker,
            'updateVoucherData': updateVoucherData,
            'bonusOutfitImage': bonusOutfitImage,
            'isBonusProductPromotion': isBonusProductPromotion,
            'isExclusivePromotion': isExclusivePromotion,
            'shouldExcludeProductPurchaseWithPromotion': shouldExcludeProductPurchaseWithPromotion,
            'isMixedBasket': isMixedBasket,
            'getBonusProductVariants': getBonusProductVariants,
            'hasBonusProductOutOfStock': hasBonusProductOutOfStock,
            'isGiftComplete': isGiftComplete
        };

        // expose selected methods
        window.basketService = {
            'init': init,
            'orderTotal': orderTotal,
            'shippingTotal': shippingTotal,
            'taxTotal': taxTotal,
            'applicableCreditCards': applicableCreditCards
        };

        return service;

        /**
         * @description Method to start initialization process to retrieve basket information
         * @param {Function} callback callback function
         * @returns {void}
         */
        function init(callback) {
            _ready = false;
            _callback = callback;
            basketService.getBasket(BASKET_ID)
                .then(basketSuccess, basketError)
                .then(() => {
                    /* Do not remove setTimeout!!
                    It is essential, otherwise the browser tab is stuck in a loading state until the request finishes execution. This loading state blocks image loading. */
                    setTimeout(() => {
                        if (isLoyaltyCustomerForCurrentCountry()) {
                            basketService.getLoyaltyVouchers().then(voucherDataSuccess, voucherDataError);
                        }
                    });
                });
        }

        /**
         * @description Method to handle successful loyalty voucher data response information.
         * @param {Object} voucherData parsed voucher data returned by the loyalty voucher service
         * @returns {Promise} a promise to be resolved.
         */
        function voucherDataSuccess(voucherData) {

            _voucherData = voucherData ? voucherData.data : null;
            _voucherDataReady = true;
            $timeout(() => {
                _voucherSectionTransition = true;
            }, 0);

        }

        /**
         * @description Method to handle successful basket response by using product items to retrieve further product information.
         * @param {Object} basketData parsed basket data returned by the basket service
         * @returns {Promise} a promise to be resolved.
         */
        function basketSuccess(basketData) {
            var defer = $q.defer(),
                productIds = basketData.products
                    .map(function(product) {
                        return product.id;
                    })
                    .filter(window.jwsdwUtil.arrayUtils.unique());


            _basketData = basketData;

            $window.jwsdwSettings.basket.id = _basketData.id;

            $log.debug('jwsdw.basket.dataService: Using following data to built the page: ', _basketData);
            productService.getProducts(productIds).then(_productDataSuccess, _productDataError).then(() => defer.resolve());

            return defer.promise;
        }

        /**
         * @description Method to handle error from getVoucher response.
         * @returns {void}
         */
        function voucherDataError() {
            _voucherRetrievalFailed = true;
        }

        /**
         * @description Method to handle error from basket response by using product items to retrieve further product information.
         * @param {Object} error error object
         * @returns {void}
         */
        function basketError(error) {
            _basketData = {
                'empty': true
            };
            $log.debug('jwsdw.basket.dataService: No basket could be loaded: ', BASKET_ID, error);
            _ready = true;
            if (typeof _callback === 'function') {
                _callback();
            }
        }

        /**
         * @description Method to handle successful product request and trigger further update logic.
         * @param {Object} productData parsed product data returned by the product service
         * @returns {Promise} a promise to be resolved.
         * @private
         */
        function _productDataSuccess(productData) {
            var defer = $q.defer();
            $log.debug('jwsdw.basket.dataService: Using following product data: ', productData);
            update().then(function() {
                _checkValidQuantities();
                _calculateTotalListPricesForSaleProducts();
                _ready = true;
                if (typeof _callback === 'function') {
                    _callback();
                }
                defer.resolve();
            });

            return defer.promise;
        }

        /**
         * @description Method to handle product data errors. This method is triggered if the product data does not
         * contain data for all products that were requested. This is used to handle deleted products.
         * @param {String[]} unavailableProductIds product ids for which no data was retrieved
         * @returns {void}
         * @private
         */
        function _productDataError(unavailableProductIds) {
            /*
             * this construct allows the sequential processing of promises.
             * This is neccessary since products that are not available anymore are deleted from the basket.
             * This changes the state of the basket. Asynchronous processing of multiple remove calls creates
             * a state problem where operations are done on outdated basket objects.
             * The array.reduce function allows to process each product id separately.
             * This works by setting the current value which is passed through every reduce callback to promise
             * and resolve it inside the callback. This creates a state were the next reduce iteration waits until the previous
             * iteration finishes and resolves the current value.
             */
            unavailableProductIds.reduce(_recheckAndDeleteUnavailableProducts, $q.resolve()).then(init);
        }

        /**
         * Method that is called with every iteration of the reduce function. It checks if the product is in the basket and then
         * rechecks if the product data cannot be retrieved and removes the product from the basket in this case.
         * @param {Object} defer promise that is resolve with every successful iteration through the reduce
         * function to ensure sequential processing
         * @param {String} productId the product id which is rechecked for availability
         * @returns {Object} promise that is resolved after successful processing of the function
         * @private
         */
        function _recheckAndDeleteUnavailableProducts(defer, productId) {
            var product = productItems().find(function (productItem) {
                return productItem.id === productId;
            });
            if (product) {
                return defer.then(function () {
                    return productService.getProduct(product.id).catch(function () {
                        return basketService.removeItem(product.basketItemId);
                    });
                });
            }
            return defer;
        }

        /**
         * @description Method to update values and broadcast new values for the basket resource.
         * @returns {Object} promise that resolves if all update operations were successful
         */
        function update() {
            var defer = $q.defer(),
                promises = [];

            _totalOrderPriceAdjustment = _getTotalOrderPriceAdjustment();
            $window.jwsdwMediator.publish('setCartCount', _basketData.cartQuantity);


            promises.push(_extendOrderPriceAdjustmentsWithPromotionData());
            promises.push(_extendBasketProductsWithProductData());
            promises.push(_updateProductAvailabilityData());

            $q.all(promises).then(function () {
                defer.resolve();
            });

            return defer.promise;
        }


        /**
        * @description Method to update voucher section
        * @param {Object} data activated voucher data
        * @returns {Object} voucher data object
        */
        function updateVoucherData(data) {
            _voucherData.hasVouchersActivated = true;

            if (data && data.voucherData && data.voucherData.Emarsys_voucher_Info) {
                _voucherData.activatedVouchers.push(data.voucherData.Emarsys_voucher_Info);
            }

            return _voucherData;
        }

        /**
        * @description Method to open selectVoucherPicker
        * @returns {void}
        */
        function openSelectVoucherPicker() {
            let eligibleVouchersArray = eligibleVouchers();
            window.jwsdwMediator.publish('openPicker', 'selectVoucherPicker',
                {
                    'eligibleVouchersArray': eligibleVouchersArray,
                    'ajaxSource': 'basket'
                });
        }

        /**
         * @description Method to get the ready state of the basket.
         * @returns {Boolean} true if basket data is ready false otherwise
         */
        function ready() {
            return _ready;
        }

        /**
        * @description Method to get the ready state of the voucher data.
        * @returns {Boolean} true if loyalty voucher data was successfully retrieved.
        */
        function voucherDataReady() {
            return _voucherDataReady;
        }

        /**
        * @description Method to get the ready state of the voucher section transition.
        * @returns {Boolean} true if basket data is ready false otherwise
        */
        function voucherSectionTransition() {
            return _voucherSectionTransition;
        }

        /**
        * @description Method to determine whether loyalty voucher information retrieval failed.
        * @returns {Boolean} true if loyalty voucher information retrieval failed.
        */
        function voucherRetrievalFailed() {
            return _voucherRetrievalFailed;
        }

        /**
         * @description Method to get the empty state of the basket.
         * @returns {Boolean} true if basket is empty false otherwise
         */
        function empty() {
            return _basketData.empty;
        }

        /**
         * @description Method to get the order total from the basket data.
         * @returns {Number} the total order price
         */
        function orderTotal() {
            return _basketData.orderTotal;
        }

        /**
         * @description Method to get the error messages from the basket data.
         * @returns {String} the error message
         */
        function errorMessages() {
            return _basketData.errorMessages;
        }
        /**
         * @description Method to get the order total from the basket data.
         * @returns {Number} the total order price
         */
        function merchandizeTotalGrossPrice() {
            return _basketData.merchandizeTotalGrossPrice;
        }

        /**
         * @description Method to get the adjusted merchandize total from the basket data.
         * @returns {Number} the total order price
         */
        function adjustedMerchandizeTotalPrice() {
            return _basketData.adjustedMerchandizeTotalPrice;
        }

        /**
         * @description Method to get the shipping total from the basket data.
         * @returns {Number} the total order shipping price
         */
        function shippingTotal() {
            return _basketData.shippingTotal;
        }


        /**
         * @description Method to get coupon-based order price adjustments from the basket data.
         * @returns {String[]} array of order price adjustments
         */
        function orderCouponPriceAdjustments() {
            return _basketData.orderCouponPriceAdjustments;
        }

        /**
         * @description Method to get order non-coupon-based price adjustments from the basket data.
         * @returns {String[]} array of order price adjustments
         */
        function orderPriceAdjustments() {
            return _basketData.orderPriceAdjustments;
        }

        /**
         * @description Method to get the total order price adjustment from the basket data.
         * @returns {Number} total order price adjustment
         */
        function totalOrderPriceAdjustment() {
            return _totalOrderPriceAdjustment;
        }

        /**
         * @description Method to get the total product level price adjustments.
         * @returns {Object} a map of total product level price adjustments.
         */
        function productLevelPriceAdjustments() {
            return _basketData.productLevelPriceAdjustments;
        }

        /**
         * @description Method to get the product items from the basket data.
         * @returns {Object[]} array of product items
         */
        function productItems() {
            return _basketData.products;
        }

        /**
         * @description Method to get the recommended product items from the basket data.
         * @returns {Object[]} array of product items
         */
        function recommendedProductItem() {
            return _basketData.recommendedProduct;
        }

        /**
         * @description Method to get the selectable bonus product items from the basket data.
         * @returns {String[]} array of product ids
         */
        function selectableBonusProducts() {
            return _basketData.selectableBonusProducts;
        }

        /**
         * @description Method to check if the basket has a bonus line item discount.
         * @returns {Boolean} true if there is a bonus line item currently in basket
         */
        function hasBonusDiscountLineItem() {
            return _basketData.hasBonusDiscountLineItem;
        }

        /**
         * @description Method to check whether to hide the product image in the bonus product banner.
         * @returns {Boolean} true if the product image in the bonus promo banner should be hidden.
         */
        function hideBonusProductImage() {
            return _basketData.hideBonusProductImage;
        }

        /**
         * @description Method to get the max number of bonus products from the basket data.
         * @returns {Number} maximum number of bonus products that can be added in basket
         */
        function maxBonusProducts() {
            return _basketData.maxBonusProducts;
        }

        /**
         * @description Method to display banner if all bonus products are out of stock
         * @returns {Boolean} true if all bonus product variants are out of stock
         */
        function outOfStockBonus() {
            return _basketData.outOfStockBonus;
        }

        /**
         * @description Method to get the coupon items from the basket data.
         * @returns {Object[]} array of coupon items
         */
        function couponItems() {
            return _basketData.coupons;
        }

        /**
         * @description Method to get the loyalty coupon items from the basket data.
         * @returns {Object[]} array of loyalty coupon items
         */
        function couponGiftCertificateItems() {
            return _basketData.couponGiftCertificates;
        }

        /**
         * @description Method to get the gift card items from the basket data.
         * @returns {Object[]} array of gift card items
         */
        function giftCardItems() {
            return _basketData.giftCards;
        }

        /**
         * @description Method to get the quantity of a basket product item
         * @param {String} basketItemId id of the basket product item (not product id)
         * @returns {Number} qunatity of the requested basket item
         */
        function productQuantity(basketItemId) {
            let basketItem = productItems().find(function(product) {
                return product.basketItemId === basketItemId;
            });

            return basketItem ? basketItem.quantity : 0;
        }

        /**
         * @description Method to check if a product is reduced through sale price or item price adjustments.
         * TODO check if in use
         * @param {Object} product product that is checked for reductions
         * @param {Boolean} product.isBonusProduct true if product is bonus product
         * @param {Number} product.price price
         * @param {Number} product.priceAfterItemDiscount price after applied discounts
         * @returns {Boolean} true if product is reduced false otherwise
         */
        function isReducedProduct(product) {
            return !product.isBonusProduct && (hasItemDiscount(product) || productService.isSaleProduct(product));
        }

        /**
         * @description Method to check if tax is included in the total price
         * @returns {Boolean} true if tax is included
         */
        function isTaxIncluded() {
            return productItems().some(function(pli) {
                return pli.taxRate > 0;
            });
        }

        /**
         * @description Method to get tax rate
         * @returns {Number|Null} tax rate of basket or null
         */
        function getTaxRate() {
            if (isTaxIncluded() && !isMixedTaxRate()) {
                return productItems()[0].taxRate;
            }
            return null;
        }

        /**
         * @description Method to check if tax rates are mixed
         * @returns {Boolean} true if tax is mixed
         */
        function isMixedTaxRate() {
            return productItems().filter(window.jwsdwUtil.arrayUtils.unique('taxRate')).length > 1;
        }

        /**
         * @description Method to check if tax is included in the total price
         * @returns {Boolean} true if tax is included
         */
        function taxTotal() {
            return _basketData.taxTotal;
        }

        /**
         * @description Method to check if the bonus product has been removed from basket
         * @returns {Boolean} true if bonus product was removed
         */
        function isRemovedBonusProduct() {
            return _basketData.isRemovedBonusProduct;
        }

        /**
         * @description Method to return default bonus product to be displayed in basket
         * @returns {Object} default selectable bonus product
         */
        function defaultSelectableBonusProduct() {
            return _basketData.defaultSelectableBonusProduct;
        }

        /**
         * @description Method to return default bonus product to be displayed in basket
         * @returns {Object} default selectable bonus product
         */
        function defaultBonusProducts() {
            return _basketData.defaultBonusProducts;
        }

        /**
         * @description Method to return currently selected bonus product line items
         * @returns {Object[]} currently selected bonus product line items
         */
        function selectedBonusProductLineItems() {
            return _basketData.selectedBonusProductLineItems;
        }

        /**
         * @description Method to check if product has an item price adjustment.
         * @param {Object} product product that is checked for discount
         * @param {Number} product.price price
         * @param {Number} product.priceAfterItemDiscount price after applied discounts
         * @returns {Boolean} true if product has item price adjustments
         */
        function hasItemDiscount(product) {
            return product.priceAfterItemDiscount < product.price;
        }

        /**
         * @description Method to get the free shipping amount from the basket data.
         * @returns {Number} amount needed to get free shipping
         */
        function freeShippingAmount () {
            return _basketData.freeShippingAmount;
        }

        /**
         * @description Method to get the free shipping amount from the basket data.
         * @returns {Number} amount needed to get free shipping
         */
        function freeShippingPercentage () {
            return _basketData.freeShippingPercentage;
        }

        /**
         * @description Method to calculate the total order price adjustment value for the basket data.
         * @returns {Number} total order price adjustment
         * @private
         */
        function _getTotalOrderPriceAdjustment() {
            var totalDiscount = 0,
                discountsLength = _basketData.orderPriceAdjustments.length,
                i;

            for (i = 0; i < discountsLength; i++) {
                totalDiscount = totalDiscount + _basketData.orderPriceAdjustments[i].price;
            }

            return totalDiscount;
        }

        /**
         * @description Method to check if object of price adjustments is empty
         * @param {Object} priceAdjustment object to check
         * @returns {Number} returns number of entries in object
         */
        function hasPriceAdjustments(priceAdjustment) {
            return Object.keys(priceAdjustment).length;
        }

        /**
         * @description Method to check if bonus product is a selected choice of bonus product line item
         * @param {String} productId id of the product to check
         * @returns {Boolean} true if bonus product is selected choice of bonus product line item false otherwise
         */
        function isChoiceOfBonusProductLineItem(productId) {
            if (_basketData.hasOwnProperty('selectedBonusProducts') && angular.isArray(_basketData.selectedBonusProducts)) {
                return _basketData.selectedBonusProducts.indexOf(productId) > -1;
            }
            return false;
        }

        /**
         * @description Method to extend the product data in the basket item with the extended product data that contains
         * availability, images, prices, links and variations and promotion data for price adjustments of product items.
         * @returns {Object} promise
         * @private
         */
        function _extendBasketProductsWithProductData() {
            var defer = $q.defer();

            _basketData.products.forEach(function(product, index) {
                productService.getProduct(product.id).then(function (productData) {
                    var shallowCopyOfProductData = JSON.parse(JSON.stringify(productData));
                    // adds the basket product data to the product data and assigns it to the basket product
                    // this is necessary since price information from the basket should not be overwritten
                    _basketData.products[index] = Object.assign(shallowCopyOfProductData, _basketData.products[index]);
                });
                _extendProductPriceAdjustmentsWithPromotionData(product, index);
            });
            $log.debug('BasketData: ', _basketData);
            defer.resolve();

            return defer.promise;
        }

        /**
         * @description Method to update product availability data (stock/ats).
         * TODO check if promise is needed
         * TODO add time stamp to prevent unnecessary reuqests
         * @returns {Object} promise
         * @private
         */
        function _updateProductAvailabilityData() {
            var defer = $q.defer();

            _basketData.products.forEach(function(product, index) {
                productService.getAvailability(product.id).then(function (availabilityData) {
                    _basketData.products[index].stock = availabilityData.inventory.ats;
                    _basketData.products[index].hasOrderableVariants = productService.hasOrderableVariants(_basketData.products[index]);
                });
            });
            $log.debug('BasketData: ', _basketData);
            defer.resolve();

            return defer.promise;
        }

        /**
         * @description Method to get promotions for an item price adjustment and add promotion data to the adjustments data.
         * This methods also checks if coupons are connected with the price adjustment.
         * TODO check if promise is needed
         * @param {Object} product basket product item that is checked for price adjustments
         * @param {Object[]} product.priceAdjustments product price adjustments
         * @param {String} product.priceAdjustments[].promotionId promotion id
         * @param {String} product.priceAdjustments[].couponCode coupon code
         * @param {Number} productIndex index of the product item in the basket data
         * @returns {Promise} promise
         * @private
         */
        function _extendProductPriceAdjustmentsWithPromotionData(product, productIndex) {
            var defer = $q.defer();

            if (product.hasOwnProperty('priceAdjustments')) {
                product.priceAdjustments.forEach(function (priceAdjustment, index) {
                    promotionService.getPromotion(priceAdjustment.promotionId).then(function (promotionData) {
                        if (promotionData.callOutMessage) {
                            _basketData.products[productIndex].priceAdjustments[index].callOutMessage = $sce.getTrustedHtml(promotionData.callOutMessage);
                        }
                        if (promotionData.promoTagName || promotionData.callOutMessage) {
                            _basketData.products[productIndex].priceAdjustments[index].promoTagName = promotionData.promoTagName || promotionData.callOutMessage;
                        }
                        if (priceAdjustment.couponCode) {
                            _setCalloutMessageToCoupon(priceAdjustment.couponCode, promotionData.callOutMessage, priceAdjustment.promotionId.includes('loyalty'));
                        }
                    });
                });
            }
            defer.resolve();

            return defer.promise;
        }

        /**
         * @description Method to get promotions for an order price adjustment and add promotion data to the adjustments data.
         * This methods also checks if coupons are connected with the price adjustment.
         * TODO check if promise is needed
         * TODO add promotion information for if a coupon is connected with the promotion
         * @returns {Object} promise
         * @private
         */
        function _extendOrderPriceAdjustmentsWithPromotionData() {
            var defer = $q.defer();

            _basketData.orderPriceAdjustments.forEach(function (priceAdjustment, index) {
                promotionService.getPromotion(priceAdjustment.promotionId).then(function (promotionData) {
                    if (promotionData.callOutMessage) {
                        _basketData.orderPriceAdjustments[index].callOutMessage = promotionData.callOutMessage;
                    }
                    if (priceAdjustment.couponCode) {
                        _setCalloutMessageToCoupon(priceAdjustment.couponCode, promotionData.callOutMessage);
                    }
                });
            });

            defer.resolve();

            return defer.promise;
        }

        /**
         * @description Method to set a callout message to the coupon item.
         * @param {String} couponCode code of the coupon to which the callout message should be added
         * @param {String} callOutMessage the callout message to add to the coupon
         * @param {Boolean} isLoyalty true if the coupon is a loyalty coupon
         * @returns {Boolean} true if a coupon was found to which the message is added false otherwise
         * @private
         */
        function _setCalloutMessageToCoupon(couponCode, callOutMessage, isLoyalty) {
            const coupons = isLoyalty ? _basketData.couponGiftCertificates : _basketData.coupons;
            const matchingCoupon = coupons.find(function (coupon) {
                return coupon.code === couponCode;
            });

            matchingCoupon.callOutMessage = matchingCoupon ? callOutMessage : null;

            return !!matchingCoupon;
        }

        // /**
        //  * @description Method to set a callout name to the coupon item.
        //  * @param {String} couponCode code of the coupon to which the callout message should be added
        //  * @param {String} couponName the callout name to add to the coupon
        //  * @returns {Boolean} true if a coupon was found to which the message is added false otherwise
        //  * @private
        //  */
        // function _setCouponNameToCoupon(couponCode, couponName) {
        //     var matchingCoupon = _basketData.coupons.find(function (coupon) {
        //         return coupon.code === couponCode;
        //     });

        //     matchingCoupon.couponName = matchingCoupon ? couponName : null;

        //     return !!matchingCoupon;
        // }

        /**
         * @description Method to calculate the total list prices for all basket sale products. Multiplies the list price with the product quantity.
         * Method is not used anymore.
         * TODO can be removed
         * @returns {void}
         * @private
         */
        function _calculateTotalListPricesForSaleProducts() {
            var listPrice;
            productItems().forEach(function (product) {
                listPrice = productService.productListPrice(product);
                product.listPrice = listPrice;
                product.totalListPrice = listPrice * product.quantity;
            });
        }

        /**
         * @description Method to check all basket product items for valid quantity values.
         * @returns {void}
         * @private
         */
        function _checkValidQuantities() {
            _quantityErrors = {};
            productItems().forEach(function (product) {
                checkValidQuantity(product.basketItemId, product.quantity, product.stock);
            });
        }

        /**
         * @description Method to check if a basket product item has a valid quantity.
         * @param {String} basketItemId id of the basket product item (not product id)
         * @param {Number} quantity current quantity of the basket item
         * @param {Number} stock current stock of the basket item
         * @returns {Boolean} true if the quantity is not 0 and lower than the stock false otherwise
         */
        function checkValidQuantity(basketItemId, quantity, stock) {
            var isValid = true;
            if (quantity > 0 && quantity <= stock) {
                _removeQuantityError(basketItemId);
            } else {
                _addQuantityError(basketItemId);
                isValid = false;
            }
            return isValid;
        }

        /**
         * @description Method to add a quantity error for a basket product item.
         * @param {String} basketItemId id of the basket product item (not product id)
         * @returns {void}
         * @private
         */
        function _addQuantityError(basketItemId) {
            if (!_quantityErrors.hasOwnProperty(basketItemId)) {
                _quantityErrors[basketItemId] = true;
            }
            service.quantityError = true;
        }

        /**
         * @description Method to remove quantity error for a basket product item.
         * @param {String} basketItemId id of the basket product item (not product id)
         * @returns {void}
         * @private
         */
        function _removeQuantityError(basketItemId) {
            if (_quantityErrors[basketItemId]) {
                delete _quantityErrors[basketItemId];
            }
            if (Object.keys(_quantityErrors).length === 0) {
                service.quantityError = false;
            }
        }

        /**
         * @description Method to get the applicablePaymentMethods from the basket data.
         * @returns {String[]} array of applicable payment methods
         */
        function applicablePaymentMethods() {
            return _basketData.applicablePaymentMethods;
        }

        /**
         * @description Method to get the isLoyaltyCustomer from the basket data.
         * @returns {Boolean} whether customer is a loyalty customer
         */
        function isLoyaltyCustomer() {
            return _basketData.isLoyaltyCustomer;
        }

        /**
         * @description Method to get the isLoyaltyCustomerForCurrentCountry from the basket data.
         * @returns {Boolean} whether customer is a loyalty customer in the current country
         */
        function isLoyaltyCustomerForCurrentCountry() {
            return _basketData.isLoyaltyCustomerForCurrentCountry;
        }

        /**
         * @description Method to get the isEmployeeAccount from the basket data.
         * @returns {Boolean} whether customer is an employee
         */
        function isEmployeeAccount() {
            return _basketData.isEmployeeAccount;
        }

        /**
         * @description Method to get the isCallawayAccount from the basket data.
         * @returns {Boolean} whether customer is an employee
         */
        function isCallawayAccount() {
            return _basketData.isCallawayAccount;
        }

        /**
         * @description Method to get the isFranchiseStore from the basket data.
         * @returns {Boolean} whether customer is an employee
         */
        function isFranchiseStore() {
            return _basketData.isFranchiseStore;
        }

        /**
         * @description Method to get the agent from the basket data.
         * @returns {Boolean} whether customer is an agent
         */
        function agent() {
            return _basketData.agent;
        }

        /**
         * @description Method to determine whether the current customer is logged in.
         * @returns {Boolean} true if the customer is logged in.
         */
        function isCustomerLoggedIn() {
            return _basketData.isCustomerLoggedIn;
        }

        /**
         * @description Method to get the original order total.
         * @returns {Number} the original order total, computed by adding all of the products' list prices.
         */
        function getOriginalOrderTotal() {
            return _basketData.originalOrderTotal;
        }

        /**
         * @description Method to get the sessionCurrency from the basket data.
         * @returns {String} currency code
         */
        function sessionCurrency() {
            return _basketData.sessionCurrency;
        }

        /**
         * @description Method to get the applicableCreditCards from the basket data.
         * @returns {Object[]} array of applicable credit cards
         */
        function applicableCreditCards() {
            return _basketData.applicableCreditCards;
        }

        /**
         * @description Method to get the non coupon promotion totals from the basket data.
         * @returns {Object} object that contains the total non coupon promotion prices for every promotion
         */
        function promotionTotal() {
            return _basketData.promotionTotal;
        }

        /**
         * @description Method to get determine whether the employee order limit has been reached.
         * @returns {Boolean} true if the order limit has been reached/exceeded.
         */
        function orderLimitReached() {
            return _basketData.orderLimitReached;
        }

        /**
         * @description Method to get the basket refusal reason.
         * @returns {String} the refusal reason code.
         */
        function refusalReason() {
            return _basketData.refusalReason;
        }

        /**
         * @description Method to get the basket order limit error message
         * @returns {String} the error message
         */
        function orderLimitMsg() {
            return _basketData.orderLimitMsg;
        }

        /**
         * @description Method to determine whether to show free shipping notification message
         * @returns {Boolean} true if after applying coupon code, free shipping is no longer available
         */
        function showShippingNotification() {
            return _basketData.showShippingNotification;
        }

        /**
         * @description Method to retrieve approaching discounts
         * @returns {Boolean} true if after applying coupon code, free shipping is no longer available
         */
        function approachingDiscounts() {
            return _basketData.approachingDiscounts;
        }


        /**
         * @description Method to get the loyalty voucher items from the basket data.
         * @returns {Object[]} array of loyalty voucher items
         */
        function basketVouchers() {
            return _voucherData ? _voucherData.basketVouchers : null;
        }

        /**
         * @description Method to retrieve if customer has loyalty vouchers available
         * @returns {Boolean} true if has loyalty vouchers available
         */
        function hasVouchersAvailable() {
            return _voucherData ? _voucherData.hasVouchersAvailable : null;
        }

        /**
         * @description Method to retrieve if customer has loyalty vouchers active
         * @returns {Boolean} true if has loyalty vouchers active
         */
        function hasVouchersActivated() {
            return _voucherData ? _voucherData.hasVouchersActivated : null;
        }

        /**
         * @description Method to retrieve all active vouchers from a customer
         * @returns {Object[]} array of active voucher items
         */
        function activatedVouchers() {
            return _voucherData ? _voucherData.activatedVouchers : null;
        }

        /**
         * @description Method to retrieve all eligible vouchers from a customer
         * @returns {Object[]} array of redeemable voucher items
         */
        function eligibleVouchers() {
            let eligibleVouchersArray = [];
            if (_voucherData) {
                eligibleVouchersArray = _voucherData.redeemableVouchers.concat(_voucherData.exclusiveVouchers);
            }
            return eligibleVouchersArray;
        }

        /**
         * @description Method to retrieve all loyalty coupon items from a customer
         * @returns {Object[]} array of active voucher items
         */
        function loyaltyCouponItems() {
            return _basketData ? _basketData.loyaltyCouponItems : [];
        }

        /**
         * @description Method to retrieve the bonus outfit image url
         * @returns {String} the bonus outfit image url
         */
        function bonusOutfitImage() {
            return _basketData.bonusOutfitImage;
        }

        /**
         * @description Method to retrieve if the promotion is bonus product
         * @returns {Boolean} true if the basket has a bonus product promotion
         */
        function isBonusProductPromotion() {
            return _basketData.isBonusProductPromotion;
        }

        /**
         * @description Method to retrieve if the promotion is exclusive
         * @returns {Boolean} true if the current promotion is exclusive
         */
        function isExclusivePromotion() {
            return _basketData.isExclusivePromotion;
        }

        /**
         * @description Method to retrieve if the promotion should be redeemed without product purchase
         * @returns {Boolean} true if the promotion should be redeemed without product purchase
         */
        function shouldExcludeProductPurchaseWithPromotion() {
            return _basketData.shouldExcludeProductPurchaseWithPromotion;
        }

        /**
         * @description Method to retrieve if the basket contains normal products and bonus products
         * @returns {Boolean} true if the basket contains normal products and bonus products
         */
        function isMixedBasket() {
            return _basketData.isMixedBasket;
        }

        /**
         * @description Method to retrieve the bonus product variants
         * @param {String} bonusProductId id of the bonus product
         * @returns {Array} array of the selected bonus product variants
         */
        function getBonusProductVariants(bonusProductId) {
            const pid = bonusProductId.split('_')[0];
            const bonusProduct = _basketData.initialSelectableBonusProducts.find(function (product) {
                return product.masterId === pid;
            });

            return bonusProduct ? bonusProduct.allBonusVariants : [];
        }

        /**
         * @description Method to check if the basket contains bonus product out of stock
         * @returns {Boolean} true if the basket contains bonus product out of stock
         */
        function hasBonusProductOutOfStock() {
            return _basketData.products.some(function (product) {
                return product.isBonusProduct && product.stock === 0;
            });
        }

        /**
         * @description Method to check if the basket contains bonus product out of stock
         * @returns {Boolean} true if the basket contains bonus product out of stock
         */
        function isGiftComplete() {
            return _basketData.isGiftComplete;
        }
    }
}(angular));
